import { forwardRef } from "react";

import {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithoutRef,
} from "@oneleet/utils/src/polymorphism";

import { ChevronRight } from "lucide-react";
import { twMerge } from "tailwind-merge";

export interface Props {
  children: React.ReactNode;
  className?: string;
  clickable?: boolean;
}

interface PolymorphicCard
  extends PolymorphicForwardRefExoticComponent<Props, "div"> {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Arrow: typeof CardArrow;
}

const BaseCard = forwardRef(
  <T extends React.ElementType = "div">(
    {
      as,
      children,
      className,
      clickable = false,
      ...rest
    }: PolymorphicPropsWithoutRef<Props, T>,
    ref?: React.ForwardedRef<Element>,
  ) => {
    const Component: React.ElementType = as ?? "div";
    return (
      <Component
        className={twMerge(
          "rounded-md border border-slate-200 bg-white px-4 py-2 shadow-sm",
          clickable && "cursor-pointer hover:bg-gray-50",
          className,
        )}
        ref={ref}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

BaseCard.displayName = "Card";

const Card: PolymorphicCard = Object.assign(BaseCard, { Arrow: CardArrow });

function CardArrow({ className }: { className?: string }) {
  return (
    <ChevronRight
      className={twMerge(
        "h-6 w-6 shrink-0 text-slate-400 group-hover:text-slate-500",
        className,
      )}
    />
  );
}

Card.Arrow = CardArrow;

export default Card;
